import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Upload, X, Heart, CheckCircle2 } from 'lucide-react';
import { API, C, apiError } from './ambApi';

export default function AmbassadorApply() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [f, setF] = useState({ first_name: '', last_name: '', email: '', phone: '', why: '', how: '', social_links: '', organization: '' });
  const [agree, setAgree] = useState(false);
  const [pic, setPic] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onPic = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { setError('Image must be JPG, PNG, or WebP'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5MB'); return; }
    setError(''); setPic(file); setPreview(URL.createObjectURL(file));
  };
  const removePic = () => { setPic(null); setPreview(null); if (fileRef.current) fileRef.current.value = ''; };

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { setError('Please agree to the ambassador terms'); return; }
    setError(''); setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append('agree_terms', 'true');
      if (pic) fd.append('profile_picture', pic);
      await axios.post(`${API}/ambassador/apply`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
    } catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };

  if (done) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: C.cream }}>
      <div className="max-w-md bg-white rounded-3xl shadow-xl p-8 text-center" data-testid="apply-success">
        <CheckCircle2 className="h-14 w-14 mx-auto mb-4" style={{ color: C.green }} />
        <h1 className="text-xl font-bold" style={{ color: C.ink }}>Application received 💛</h1>
        <p className="mt-3 text-[15px]" style={{ color: '#4A5560' }}>Thank you for applying to become an EasyPassing Legacy Ambassador. We will review your application and contact you soon.</p>
        <button onClick={() => navigate('/')} className="mt-6 px-6 h-11 rounded-full text-white font-semibold" style={{ background: C.navy }}>Back to home</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: C.cream }}>
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <p className="text-sm font-bold tracking-wide" style={{ color: C.gold }}>EASYPASSING</p>
          <h1 className="text-3xl font-bold mt-1" style={{ color: C.navy }}>Become a Legacy Ambassador</h1>
          <p className="mt-2 text-[15px]" style={{ color: '#4A5560' }}>Earn a $10 bonus + 10% monthly commission for every family you help protect.</p>
        </div>
        <form onSubmit={submit} data-testid="ambassador-apply-form" className="bg-white rounded-3xl shadow-lg p-7 space-y-4">
          {error && <div className="text-sm rounded-xl bg-red-50 border border-red-200 text-red-800 px-3 py-2.5" data-testid="apply-error">{error}</div>}
          <div className="flex flex-col items-center">
            {preview
              ? <div className="relative"><img src={preview} alt="preview" className="w-24 h-24 rounded-full object-cover" /><button type="button" onClick={removePic} className="absolute -top-1 -right-1 bg-white rounded-full border p-1"><X className="h-4 w-4" /></button></div>
              : <button type="button" onClick={() => fileRef.current?.click()} className="w-24 h-24 rounded-full border-2 border-dashed flex flex-col items-center justify-center text-xs" style={{ borderColor: C.line, color: C.mute }}><Upload className="h-5 w-5 mb-1" />Photo</button>}
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPic} className="hidden" data-testid="apply-photo-input" />
            <p className="text-xs mt-2" style={{ color: C.mute }}>Optional · JPG/PNG/WebP · max 5MB · square recommended</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <In label="First name" v={f.first_name} onChange={set('first_name')} required testid="apply-first" />
            <In label="Last name" v={f.last_name} onChange={set('last_name')} required testid="apply-last" />
          </div>
          <In label="Email" type="email" v={f.email} onChange={set('email')} required testid="apply-email" />
          <In label="Phone" v={f.phone} onChange={set('phone')} testid="apply-phone" />
          <Ta label="Why do you want to become an EasyPassing Ambassador?" v={f.why} onChange={set('why')} />
          <Ta label="How do you plan to promote EasyPassing?" v={f.how} onChange={set('how')} />
          <In label="Social media links" v={f.social_links} onChange={set('social_links')} />
          <In label="Organization or business name (optional)" v={f.organization} onChange={set('organization')} />
          <label className="flex items-start gap-2 text-sm" style={{ color: '#4A5560' }}>
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="apply-agree" />
            <span>I agree to the <a href="/ambassador/terms" target="_blank" rel="noreferrer" style={{ color: C.navy2, fontWeight: 600, textDecoration: 'underline' }} data-testid="apply-terms-link">EasyPassing Ambassador Terms &amp; Commission Policy</a>.</span>
          </label>
          <button type="submit" disabled={loading} data-testid="apply-submit" className="w-full h-12 rounded-full text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }}>
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Apply to Become an Ambassador'}
          </button>
        </form>
      </div>
    </div>
  );
}

const In = ({ label, v, onChange, type = 'text', required, testid }) => (
  <div><label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>{label}</label>
    <input type={type} value={v} onChange={onChange} required={required} data-testid={testid}
      className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none focus:border-[#2B4C7E]" /></div>
);
const Ta = ({ label, v, onChange }) => (
  <div><label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>{label}</label>
    <textarea value={v} onChange={onChange} rows={2} className="w-full rounded-xl border border-[#E3E1DC] p-3 text-sm outline-none focus:border-[#2B4C7E]" /></div>
);
